import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface Campaign {
  _id: string;
  name: string;
  subject: string;
  template?: string; // Old system - filename
  templateId?: string; // New system - ID reference
  targetSegment: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled';
  analytics: {
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    unsubscribed: number;
  };
  createdAt: string;
  sentAt?: string;
  scheduledDate?: string;
}

interface EmailTemplate {
  _id: string;
  name: string;
  description: string;
  subject: string;
  htmlContent: string;
  textContent?: string;
  category: 'automated' | 'manual' | 'welcome' | 'tips' | 'feedback' | 'reactivation';
  variables: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UserSegments {
  all: number;
  new_users: number;
  active_users: number;
  inactive_users: number;
  golden_dose_users: number;
}

interface EmailAnalytics {
  totalSubscribers: number;
  averageEngagement: string;
  campaigns: Array<{
    name: string;
    subject: string;
    sent: number;
    openRate: string;
    clickRate: string;
    status: string;
    sentAt?: string;
  }>;
}

const AdminEmailCampaignsPage: React.FC = () => {
  const { isAuthenticated, userType } = useAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [segments, setSegments] = useState<UserSegments | null>(null);
  const [analytics, setAnalytics] = useState<EmailAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'campaigns' | 'templates' | 'analytics'>('overview');
  
  // New campaign modal state
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [newCampaignData, setNewCampaignData] = useState({
    name: '',
    subject: '',
    templateId: '', // Use templateId instead of template filename
    targetSegment: 'all' // Default to 'all', will be updated when segments load
  });
  const [creating, setCreating] = useState(false);

  // Template management state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    description: '',
    subject: '',
    htmlContent: '',
    textContent: '',
    category: 'manual' as 'automated' | 'manual' | 'welcome' | 'tips' | 'feedback' | 'reactivation'
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templatePreviewTab, setTemplatePreviewTab] = useState<'edit' | 'preview'>('edit');

  const isAdmin = userType === 'admin';

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;
    fetchData();
  }, [isAuthenticated, isAdmin]);

  // Update default targetSegment when segments are loaded
  useEffect(() => {
    if (segments && Object.keys(segments).length > 0) {
      // Prefer 'all' if it exists, otherwise use the first available segment
      const defaultSegment = segments.all !== undefined ? 'all' : Object.keys(segments)[0];
      setNewCampaignData(prev => ({
        ...prev,
        targetSegment: prev.targetSegment === 'all' || !(prev.targetSegment in segments) 
          ? defaultSegment 
          : prev.targetSegment
      }));
    }
  }, [segments]);

  // Update default templateId when templates are loaded
  useEffect(() => {
    if (templates.length > 0 && !newCampaignData.templateId) {
      // Default to the first manual category template, or just the first template
      const defaultTemplate = templates.find(t => t.category === 'manual') || templates[0];
      setNewCampaignData(prev => ({
        ...prev,
        templateId: defaultTemplate._id
      }));
    }
  }, [templates, newCampaignData.templateId]);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const [campaignsRes, templatesRes, segmentsRes, analyticsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/admin/email-campaigns`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/admin/email-templates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/admin/email-segments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/admin/email-analytics`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (campaignsRes.ok) {
        const campaignsData = await campaignsRes.json();
        setCampaigns(campaignsData);
      }

      if (templatesRes.ok) {
        const templatesData = await templatesRes.json();
        setTemplates(templatesData);
      }

      if (segmentsRes.ok) {
        const segmentsData = await segmentsRes.json();
        setSegments(segmentsData);
      }

      if (analyticsRes.ok) {
        const analyticsData = await analyticsRes.json();
        setAnalytics(analyticsData);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async () => {
    if (!newCampaignData.name || !newCampaignData.subject || !newCampaignData.templateId) {
      setError('Campaign name, subject, and template are required');
      return;
    }

    setCreating(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/admin/email-campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newCampaignData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create campaign');
      }

      setSuccess('Campaign created successfully!');
      setShowNewCampaign(false);
      setNewCampaignData({ 
        name: '', 
        subject: '', 
        templateId: templates.length > 0 ? templates[0]._id : '', 
        targetSegment: segments ? Object.keys(segments)[0] || 'all' : 'all'
      });
      await fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const sendCampaign = async (campaignId: string) => {
    if (!window.confirm('Are you sure you want to send this campaign? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/admin/email-campaigns/${campaignId}/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send campaign');
      }

      setSuccess('Campaign sent successfully!');
      await fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (templateId: string) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/admin/email-templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete template');
      }

      setSuccess('Template deleted successfully!');
      await fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = async () => {
    if (!templateFormData.name || !templateFormData.description || !templateFormData.subject || !templateFormData.htmlContent) {
      setError('Name, description, subject, and HTML content are required');
      return;
    }

    setSavingTemplate(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/admin/email-templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(templateFormData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create template');
      }

      setSuccess('Template created successfully!');
      setShowTemplateModal(false);
      setTemplateFormData({
        name: '',
        description: '',
        subject: '',
        htmlContent: '',
        textContent: '',
        category: 'manual'
      });
      await fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  const updateTemplate = async () => {
    if (!editingTemplate || !templateFormData.name || !templateFormData.description || !templateFormData.subject || !templateFormData.htmlContent) {
      setError('Name, description, subject, and HTML content are required');
      return;
    }

    setSavingTemplate(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/admin/email-templates/${editingTemplate._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(templateFormData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update template');
      }

      setSuccess('Template updated successfully!');
      setShowTemplateModal(false);
      setEditingTemplate(null);
      await fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'bg-green-100 text-green-800';
      case 'sending': return 'bg-blue-100 text-blue-800';
      case 'scheduled': return 'bg-yellow-100 text-yellow-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isAuthenticated) {
    return <div className="p-8 text-center">Please log in as an admin to access this page.</div>;
  }
  
  if (!isAdmin) {
    return <div className="p-8 text-center text-red-600">Access denied. Admins only.</div>;
  }

  return (
    <div className="max-w-7xl mx-auto p-8">
      <h1 className="text-4xl font-bold mb-8 font-['Space_Grotesk'] bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Admin: Email Campaigns</h1>
      
      {/* Tab Navigation */}
      <div className="flex mb-6 border-b">
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'overview' ? 'text-emerald-600 border-b-2 border-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'campaigns' ? 'text-emerald-600 border-b-2 border-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setActiveTab('campaigns')}
        >
          Campaigns
        </button>
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'templates' ? 'text-emerald-600 border-b-2 border-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setActiveTab('templates')}
        >
          Templates
        </button>
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'analytics' ? 'text-emerald-600 border-b-2 border-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setActiveTab('analytics')}
        >
          Analytics
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading email data...</div>
      ) : error ? (
        <div className="text-red-600 mb-4 p-4 bg-red-50 rounded">{error}</div>
      ) : (
        <div className="space-y-8">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Subscribers</p>
                      <p className="text-2xl font-semibold text-gray-900">{analytics?.totalSubscribers || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Campaigns</p>
                      <p className="text-2xl font-semibold text-gray-900">{campaigns.length}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Sent Campaigns</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {campaigns.filter(c => c.status === 'sent').length}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="flex items-center">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Avg Engagement</p>
                      <p className="text-2xl font-semibold text-gray-900">{analytics?.averageEngagement || 0}%</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* User Segments */}
              {segments && (
                <div className="bg-white p-6 rounded-lg shadow">
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">User Segments</h3>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-600">{segments.all}</p>
                      <p className="text-sm text-gray-600">All Users</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{segments.new_users}</p>
                      <p className="text-sm text-gray-600">New Users</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{segments.active_users}</p>
                      <p className="text-sm text-gray-600">Active Users</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-yellow-600">{segments.inactive_users}</p>
                      <p className="text-sm text-gray-600">Inactive Users</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-600">{segments.golden_dose_users}</p>
                      <p className="text-sm text-gray-600">Golden Dose Users</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button
                    onClick={() => setShowNewCampaign(true)}
                    className="bg-gradient-to-br from-emerald-500 to-teal-500 text-white p-6 rounded-2xl hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 text-center shadow-lg hover:shadow-xl hover:-translate-y-1"
                  >
                    <div className="text-2xl mb-2">📧</div>
                    <div className="font-semibold">Create Campaign</div>
                    <div className="text-sm opacity-90">Send emails to your users</div>
                  </button>
                  <button
                    onClick={() => setActiveTab('analytics')}
                    className="bg-blue-600 text-white p-6 rounded-lg hover:bg-blue-700 transition-colors text-center"
                  >
                    <div className="text-2xl mb-2">📊</div>
                    <div className="font-semibold">View Analytics</div>
                    <div className="text-sm opacity-90">Email performance metrics</div>
                  </button>
                  <button
                    onClick={() => setActiveTab('campaigns')}
                    className="bg-purple-600 text-white p-6 rounded-lg hover:bg-purple-700 transition-colors text-center"
                  >
                    <div className="text-2xl mb-2">📝</div>
                    <div className="font-semibold">Manage Campaigns</div>
                    <div className="text-sm opacity-90">View and edit campaigns</div>
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Campaigns Tab */}
          {activeTab === 'campaigns' && (
            <>
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-stone-900">Email Campaigns</h2>
                <button
                  onClick={() => setShowNewCampaign(true)}
                  className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  Create Campaign
                </button>
              </div>

              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-emerald-50">
                        <th className="p-4 text-left border-b font-semibold text-emerald-800">Campaign</th>
                        <th className="p-4 text-left border-b font-semibold text-emerald-800">Subject</th>
                        <th className="p-4 text-left border-b font-semibold text-emerald-800">Target</th>
                        <th className="p-4 text-left border-b font-semibold text-emerald-800">Status</th>
                        <th className="p-4 text-left border-b font-semibold text-emerald-800">Sent</th>
                        <th className="p-4 text-left border-b font-semibold text-emerald-800">Open Rate</th>
                        <th className="p-4 text-left border-b font-semibold text-emerald-800">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((campaign) => (
                        <tr key={campaign._id} className="border-b hover:bg-gray-50">
                          <td className="p-4 font-medium">{campaign.name}</td>
                          <td className="p-4">{campaign.subject}</td>
                          <td className="p-4">{campaign.targetSegment.replace('_', ' ')}</td>
                          <td className="p-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                              {campaign.status}
                            </span>
                          </td>
                          <td className="p-4">{campaign.analytics.sent}</td>
                          <td className="p-4">
                            {campaign.analytics.sent > 0 
                              ? `${((campaign.analytics.opened / campaign.analytics.sent) * 100).toFixed(1)}%`
                              : '-'
                            }
                          </td>
                          <td className="p-4">
                            {campaign.status === 'draft' && (
                              <button
                                onClick={() => sendCampaign(campaign._id)}
                                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                              >
                                Send
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <>
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-stone-900">Email Templates</h2>
                <button
                  onClick={() => {
                    setEditingTemplate(null);
                    setTemplateFormData({
                      name: '',
                      description: '',
                      subject: '',
                      htmlContent: '',
                      textContent: '',
                      category: 'manual'
                    });
                    setShowTemplateModal(true);
                  }}
                  className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  Create Template
                </button>
              </div>

              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-emerald-50">
                        <th className="p-4 text-left border-b font-semibold text-emerald-800">Template Name</th>
                        <th className="p-4 text-left border-b font-semibold text-emerald-800">Category</th>
                        <th className="p-4 text-left border-b font-semibold text-emerald-800">Subject</th>
                        <th className="p-4 text-left border-b font-semibold text-emerald-800">Variables</th>
                        <th className="p-4 text-left border-b font-semibold text-emerald-800">Updated</th>
                        <th className="p-4 text-left border-b font-semibold text-emerald-800">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map(template => (
                        <tr key={template._id} className="hover:bg-gray-50">
                          <td className="p-4 border-b">
                            <div>
                              <div className="font-semibold">{template.name}</div>
                              <div className="text-sm text-gray-600">{template.description}</div>
                            </div>
                          </td>
                          <td className="p-4 border-b">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              template.category === 'manual' ? 'bg-blue-100 text-blue-800' :
                              template.category === 'welcome' ? 'bg-green-100 text-green-800' :
                              template.category === 'tips' ? 'bg-yellow-100 text-yellow-800' :
                              template.category === 'automated' ? 'bg-purple-100 text-purple-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {template.category}
                            </span>
                          </td>
                          <td className="p-4 border-b">
                            <div className="truncate max-w-xs" title={template.subject}>
                              {template.subject}
                            </div>
                          </td>
                          <td className="p-4 border-b">
                            <div className="text-sm text-gray-600">
                              {template.variables && template.variables.length > 0 ? (
                                <span title={template.variables.map(v => `{{${v}}}`).join(', ')}>
                                  {template.variables.length} variable{template.variables.length !== 1 ? 's' : ''}
                                </span>
                              ) : (
                                'No variables'
                              )}
                            </div>
                          </td>
                          <td className="p-4 border-b">
                            <div className="text-sm text-gray-600">
                              {new Date(template.updatedAt).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="p-4 border-b">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => {
                                  setEditingTemplate(template);
                                  setTemplateFormData({
                                    name: template.name,
                                    description: template.description,
                                    subject: template.subject,
                                    htmlContent: template.htmlContent,
                                    textContent: template.textContent || '',
                                    category: template.category
                                  });
                                  setShowTemplateModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => {
                                  if (window.confirm(`Are you sure you want to delete the template "${template.name}"?`)) {
                                    deleteTemplate(template._id);
                                  }
                                }}
                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {templates.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No templates found. Create your first template to get started.
                  </div>
                )}
              </div>
            </>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && analytics && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-stone-900">Email Analytics</h2>
              
              {analytics.campaigns.length > 0 ? (
                <div className="bg-white p-6 rounded-lg shadow">
                  <h3 className="text-lg font-semibold mb-4">Recent Campaign Performance</h3>
                  <div className="space-y-4">
                    {analytics.campaigns.map((campaign, index) => (
                      <div key={index} className="border-b pb-4 last:border-b-0">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium">{campaign.name}</h4>
                            <p className="text-sm text-gray-600">{campaign.subject}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">
                              {campaign.sentAt ? formatDate(campaign.sentAt) : 'Not sent'}
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Sent: </span>
                            <span className="font-medium">{campaign.sent}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Open Rate: </span>
                            <span className="font-medium">{campaign.openRate}%</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Click Rate: </span>
                            <span className="font-medium">{campaign.clickRate}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-white p-6 rounded-lg shadow text-center">
                  <p className="text-gray-600">No campaigns sent yet. Create your first campaign to see analytics.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* New Campaign Modal */}
      {showNewCampaign && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Create New Campaign</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Campaign Name</label>
                <input
                  type="text"
                  value={newCampaignData.name}
                  onChange={(e) => setNewCampaignData({ ...newCampaignData, name: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="e.g., Weekly Tips - January 2025"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email Subject</label>
                <input
                  type="text"
                  value={newCampaignData.subject}
                  onChange={(e) => setNewCampaignData({ ...newCampaignData, subject: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="e.g., Weekly Mounjaro Tips from DoseWise"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email Template</label>
                <select
                  value={newCampaignData.templateId}
                  onChange={(e) => setNewCampaignData({ ...newCampaignData, templateId: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">Select a template...</option>
                  {templates.map(template => (
                    <option key={template._id} value={template._id}>
                      {template.name} ({template.category})
                    </option>
                  ))}
                </select>
                {newCampaignData.templateId && templates.length > 0 && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-md">
                    {(() => {
                      const selectedTemplate = templates.find(t => t._id === newCampaignData.templateId);
                      return selectedTemplate ? (
                        <div>
                          <p className="text-sm text-gray-600 mb-1">
                            <strong>Description:</strong> {selectedTemplate.description}
                          </p>
                          <p className="text-sm text-gray-600 mb-1">
                            <strong>Default Subject:</strong> {selectedTemplate.subject}
                            <button
                              type="button"
                              onClick={() => setNewCampaignData(prev => ({ ...prev, subject: selectedTemplate.subject }))}
                              className="ml-2 text-xs text-blue-600 hover:text-blue-800"
                            >
                              Use This
                            </button>
                          </p>
                          {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                            <p className="text-sm text-gray-600">
                              <strong>Variables:</strong> {selectedTemplate.variables.map(v => `{{${v}}}`).join(', ')}
                            </p>
                          )}
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Target Audience</label>
                <select
                  value={newCampaignData.targetSegment}
                  onChange={(e) => setNewCampaignData({ ...newCampaignData, targetSegment: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                >
                  {segments && Object.entries(segments).map(([key, count]) => {
                    // Dynamically convert any segment key to proper display name
                    const getDisplayName = (segmentKey: string) => {
                      // Convert snake_case to Title Case and add "Users"
                      if (segmentKey === 'all') return 'All Users';
                      
                      return segmentKey
                        .split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ') + ' Users';
                    };

                    return (
                      <option key={key} value={key}>
                        {getDisplayName(key)} ({count})
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowNewCampaign(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createCampaign}
                disabled={creating}
                className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:bg-gray-400"
              >
                {creating ? 'Creating...' : 'Create Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Editor Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {editingTemplate ? 'Edit Template' : 'Create New Template'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column - Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Template Name</label>
                  <input
                    type="text"
                    value={templateFormData.name}
                    onChange={(e) => setTemplateFormData({ ...templateFormData, name: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="e.g., Monthly Newsletter"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <input
                    type="text"
                    value={templateFormData.description}
                    onChange={(e) => setTemplateFormData({ ...templateFormData, description: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Brief description of when to use this template"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Default Subject</label>
                  <input
                    type="text"
                    value={templateFormData.subject}
                    onChange={(e) => setTemplateFormData({ ...templateFormData, subject: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Default email subject line"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <select
                    value={templateFormData.category}
                    onChange={(e) => setTemplateFormData({ ...templateFormData, category: e.target.value as any })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="manual">Manual</option>
                    <option value="welcome">Welcome</option>
                    <option value="tips">Tips</option>
                    <option value="feedback">Feedback</option>
                    <option value="automated">Automated</option>
                    <option value="reactivation">Reactivation</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Plain Text Version (Optional)</label>
                  <textarea
                    value={templateFormData.textContent}
                    onChange={(e) => setTemplateFormData({ ...templateFormData, textContent: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                    rows={4}
                    placeholder="Plain text version for email clients that don't support HTML"
                  />
                </div>
              </div>
              
              {/* Right Column - HTML Editor */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">HTML Content</label>
                  <div className="flex items-center gap-2 text-sm mb-2">
                    <button
                      type="button"
                      onClick={() => setTemplatePreviewTab('edit')}
                      className={`px-3 py-1 rounded ${templatePreviewTab === 'edit' ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                      Edit HTML
                    </button>
                    <button
                      type="button"
                      onClick={() => setTemplatePreviewTab('preview')}
                      className={`px-3 py-1 rounded ${templatePreviewTab === 'preview' ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                      Preview
                    </button>
                  </div>
                  {templatePreviewTab === 'edit' ? (
                    <textarea
                      value={templateFormData.htmlContent}
                      onChange={(e) => setTemplateFormData({ ...templateFormData, htmlContent: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500 font-mono text-sm"
                      rows={20}
                      placeholder="Enter your HTML email template here..."
                    />
                  ) : (
                    <div className="mt-1 border rounded-md overflow-hidden bg-gray-50">
                      <div className="bg-gray-100 text-gray-700 text-xs px-3 py-2">Preview (640px width)</div>
                      <div className="p-3 flex justify-center">
                        <iframe
                          title="Email Preview"
                          style={{ width: '640px', minHeight: '480px', border: '1px solid #e5e7eb', background: 'white' }}
                          sandbox="allow-same-origin"
                          srcDoc={templateFormData.htmlContent || '<div style="padding:24px;font-family:Arial,Helvetica,sans-serif;color:#334155;">No HTML content yet…</div>'}
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Variable Helper */}
                <div className="bg-blue-50 p-3 rounded-md">
                  <h4 className="font-semibold text-blue-800 mb-2">Available Variables:</h4>
                  <div className="text-sm text-blue-700 space-y-1">
                    <div><code>{`{{user_email}}`}</code> - User's email address</div>
                    <div><code>{`{{user_country}}`}</code> - User's country</div>
                    <div><code>{`{{user_name}}`}</code> - User's name (from email)</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={editingTemplate ? updateTemplate : createTemplate}
                disabled={savingTemplate}
                className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:bg-gray-400"
              >
                {savingTemplate ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded shadow-lg z-50">
          {success}
        </div>
      )}
    </div>
  );
};

export default AdminEmailCampaignsPage; 